import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CopySimple, PencilSimple, Printer } from "@phosphor-icons/react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import moment from "moment";
import PageFrame from "../../common/PageFrame";
import Loader from "../../common/Loader";
import SecondaryButton from "../../assets/components/SecondaryButton";
import { eventActions } from "../../redux/actions";

const EventDetail = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const noqUser = localStorage.getItem("noqEventUser") || sessionStorage.getItem("noqEventUser");
    const userObj = JSON.parse(noqUser || "{}");
    const [eventDetails, setEventDetails] = useState({});

    const {
        isFetchingSingleEvent,
        isFetchedSingleEventSuccess,
        isPublishingEvent,
        isPublishedEventSuccess,
        isUnpublishingEvent,
        isUnpublishedEventSuccess,
    } = useSelector((store) => store.eventManager);

    const isPublished = eventDetails?.status?.toLowerCase() === "published";
    const eventAddress = [
        eventDetails?.street,
        eventDetails?.city,
        eventDetails?.postal_code,
        eventDetails?.country,
    ].filter(Boolean).join(", ");

    const copyText = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Event link copied");
    };

    const printQrCode = () => {
        const qrCodeContainer = document.querySelector("#qrcode-container");
        if (!qrCodeContainer) return;

        const printWindow = window.open("", "_blank");
        if (!printWindow) return;

        printWindow.document.write(`
            <html>
                <head>
                    <title>Event QR Code</title>
                    <style>
                        body { margin: 0; display: grid; place-items: center; min-height: 100vh; background: #f0f0f0; font-family: Arial, sans-serif; }
                        .qr-code-container { text-align: center; background: white; padding: 32px; border-radius: 12px; }
                        h1 { margin: 0 0 12px; font-size: 30px; color: #171717; }
                        p { margin: 0 0 20px; color: #555; }
                        .powered-by { margin-top: 20px; color: #777; font-size: 13px; }
                    </style>
                </head>
                <body>
                    <div class="qr-code-container">
                        <h1>${eventDetails?.name || "Event"}</h1>
                        <p>Scan to find vendors</p>
                        ${qrCodeContainer.innerHTML}
                        <div class="powered-by">Powered by NO-Q · www.no-q.ca</div>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const handlePublishEvent = () => {
        dispatch(eventActions.publishEvent(params?.eventId, userObj?.token));
    };

    const handleUnpublishEvent = () => {
        dispatch(eventActions.unpublishEvent(params?.eventId, userObj?.token));
    };

    useEffect(() => {
        if (params?.eventId) dispatch(eventActions.getSingleEvent(params.eventId));
    }, [dispatch, params?.eventId]);

    useEffect(() => {
        if (isFetchedSingleEventSuccess) setEventDetails(isFetchedSingleEventSuccess);
    }, [isFetchedSingleEventSuccess]);

    useEffect(() => {
        if (isPublishedEventSuccess) {
            toast.success("Event published successfully");
            setEventDetails((current) => ({ ...current, status: isPublishedEventSuccess?.status }));
            dispatch(eventActions.resetPublishEventSuccess());
        }
    }, [isPublishedEventSuccess, dispatch]);

    useEffect(() => {
        if (isUnpublishedEventSuccess) {
            toast.success("Event unpublished successfully");
            setEventDetails((current) => ({ ...current, status: isUnpublishedEventSuccess?.status }));
            dispatch(eventActions.resetUnpublishEventSuccess());
        }
    }, [isUnpublishedEventSuccess, dispatch]);

    return (
        <PageFrame
            isLoading={isFetchingSingleEvent}
            frameLinks={[
                { text: "Event Details", url: `/dashboard/event/${params?.eventId}`, isActive: true },
                { text: "Vendors & invitations", url: `/dashboard/event/${params?.eventId}/invitations`, isActive: false },
                { text: "Event insights", url: `/dashboard/event/${params?.eventId}/insights`, isActive: false },
            ]}
            childrenContainerClass="event-detail-main"
        >
            <div className="event-detail-page">
                <header className="event-detail-header">
                    <div className="event-detail-title-row">
                        <div className="event-detail-title">
                            <h1>{eventDetails?.name || "Event details"}</h1>
                            <span className={`event-state-badge ${isPublished ? "is-active" : ""}`}>
                                {isPublished ? "Active" : "Inactive"}
                            </span>
                        </div>
                        <SecondaryButton className="event-secondary-button" onClick={() => navigate(`/dashboard/event/${params?.eventId}/edit`)}>
                            <PencilSimple size={18} weight="bold" />
                            Edit event
                        </SecondaryButton>
                    </div>
                </header>

                <div className="event-detail-grid">
                    <section className="event-panel event-qr-panel">
                        <div className="event-panel__heading">
                            <h2>QR code</h2>
                            <p>Print and display this code so guests can find vendors from their phones.</p>
                        </div>

                        <div className="event-qr-stage">
                            <div id="qrcode-container" className="qrcode-container" dangerouslySetInnerHTML={{ __html: eventDetails?.qr_code }} />
                        </div>

                        <SecondaryButton onClick={printQrCode} className="event-secondary-button event-wide-secondary">
                            <Printer size={18} weight="bold" />
                            Print QR code
                        </SecondaryButton>
                    </section>

                    <div className="event-detail-side">
                        <section className="event-panel event-url-panel">
                            <div className="event-panel__heading">
                                <h2>Event URL</h2>
                                <p>Share this link with guests so they can browse participating vendors.</p>
                            </div>
                            <div className="event-url-row">
                                <div className="event-url-value" title={eventDetails?.url}>{eventDetails?.url || "Event link unavailable"}</div>
                                <SecondaryButton className="event-secondary-button" onClick={() => copyText(eventDetails?.url)} disabled={!eventDetails?.url}>
                                    <CopySimple size={18} weight="bold" />
                                    Copy
                                </SecondaryButton>
                            </div>
                        </section>

                        <section className="event-panel event-overview-panel">
                            <div className="event-panel__heading"><h2>Event information</h2></div>
                            <dl className="event-facts">
                                <div><dt>Address</dt><dd>{eventAddress || "No address provided"}</dd></div>
                                <div><dt>Starts</dt><dd>{eventDetails?.start_date ? moment(eventDetails.start_date).format("MMM DD, YYYY") : "Not set"}</dd></div>
                                <div><dt>Ends</dt><dd>{eventDetails?.end_date ? moment(eventDetails.end_date).format("MMM DD, YYYY") : "Not set"}</dd></div>
                            </dl>
                        </section>

                        <section className="event-panel event-status-panel">
                            <div className="event-status-copy">
                                <div className="event-status-heading">
                                    <h2>Event status</h2>
                                    <span className={`event-state-badge ${isPublished ? "is-active" : ""}`}>{isPublished ? "Active" : "Inactive"}</span>
                                </div>
                                <p>{isPublished ? "This event is visible and accessible to guests." : "Publish this event when it is ready for guests."}</p>
                            </div>
                            <button
                                type="button"
                                className={`event-status-action ${isPublished ? "is-danger" : "is-publish"}`}
                                disabled={isPublishingEvent || isUnpublishingEvent}
                                onClick={isPublished ? handleUnpublishEvent : handlePublishEvent}
                            >
                                {isPublished ? "Take event offline" : "Publish event"}
                                {(isPublishingEvent || isUnpublishingEvent) && <Loader size={4} color={isPublished ? "white" : "black"} />}
                            </button>
                        </section>
                    </div>
                </div>
            </div>
        </PageFrame>
    );
};

export default EventDetail;
